// @ts-check
import { AUTOTESTS_ID_ATTRIBUTE, SIMPLE_BAR_SCROLL } from '../constants/index.js';
import { createElement } from '../helpers/dom-elements.js';
import { clearContents, initForMedia } from '../utils/index.js';
import { getSiteConfiguration } from '../site-configuration.js';
import * as pubSub from '../utils/pub-sub.js';
import { createPesIcon } from '../../utils/utils';
/**
 * @typedef {import('../constants/events').SimpleBarScrollEvent} SimpleBarScrollEvent
 */

const SCROLL_OFFSET = {
    desktop: {
        top: '20',
        bottom: '20'
    }
};

export const elements = {
    /** @type {HTMLFormElement} */
    searchForm: null,
    /** @type {HTMLInputElement} */
    zipCodeInput: null,
    /** @type {HTMLDivElement} */
    distributorList: null,
    /** @type {HTMLDivElement} */
    modalBody: null
};

export function getZipCodeInput() {
    return elements.zipCodeInput;
}

export function getSearchForm() {
    return elements.searchForm;
}

/**
 * @param {object} params
 * @param {object} params.labels
 * @param {string} params.labels.title
 * @param {string} params.labels.placeholder
 * @param {string} params.labels.button
 * @param {string} params.labels.invalidZipCodeError
 * @returns {HTMLFormElement}
 */
export function renderSearchForm(params) {
    const { labels: {
        title,
        placeholder,
        button,
        invalidZipCodeError
    } } = params;

    const titleElement = createElement('div', {
        className: 'cc-search-block__title',
        innerText: title
    });

    elements.zipCodeInput = createElement('input', {
        className: 'cc-search-block__input',
        type: 'text',
        placeholder
    });

    const control = createElement('div', {
      className: 'cc-search-block__control',
      contents: [
        createElement('span', {
            className: 'cc-search-block__input-icon',
            contents: createPesIcon({
                iconName: 'map_location',
                size: 'SMALL',
            })
        }),
        elements.zipCodeInput,
        createElement('button', {
          className: 'cc-search-block__button btn btn-primary',
          innerHTML: button,
          type: 'submit'
        })
      ]
    });

    const validationMessage = createElement('div', {
        className: 'cc-search-block__validation-message',
        innerHTML: invalidZipCodeError,
        attributes: {
            [AUTOTESTS_ID_ATTRIBUTE]: 'commerce-connector-zip-validation-message'
        }
    });

    elements.searchForm = createElement('form', {
        className: 'cc-search-block',
        contents: [
            titleElement,
            control,
            validationMessage
        ]
    });

    return elements.searchForm;
}

let previousScrollYOffset = 0;

/**
 * @param {SimpleBarScrollEvent} event
 */
function onModalBodyScroll({ yOffset }) {
    if (yOffset === 0) {
        elements.modalBody.classList.remove('cc-modal__body--scrolled');
    } else if (previousScrollYOffset === 0) {
        elements.modalBody.classList.add('cc-modal__body--scrolled');
    }

    previousScrollYOffset = yOffset;
}

/**
 * @param {HTMLElement[]} children
 * @returns {HTMLElement}
 */
export function renderModalBody(children = []) {
    elements.distributorList = createElement('div', { className: 'cc-distributors-list__content' });

    const { enableRightToLeftLayout } = getSiteConfiguration();

    const scrollContainer = createElement('div', {
        contents: [...children, elements.distributorList],
        dataset: {
            simplebar: 'null',
            simplebarAutoHide: 'false',
            simplebarVerticalOffsetTop: SCROLL_OFFSET.desktop.top,
            simplebarVerticalOffsetBottom: SCROLL_OFFSET.desktop.bottom,
            ...(enableRightToLeftLayout && { simplebarDirection: 'rtl' })
        }
    });

    initForMedia({
        onDesktopToTablet() {
            pubSub.unsubscribe(SIMPLE_BAR_SCROLL, onModalBodyScroll);
        },
        onTabletToDesktop() {
            pubSub.subscribe(SIMPLE_BAR_SCROLL, onModalBodyScroll, { unsubscribePrevious: true });
        }
    });

    elements.modalBody = createElement('div', {
        className: 'cc-modal__body',
        contents: createElement('div', {
            className: 'cc-distributors-list',
            contents: scrollContainer
        })
    });

    return elements.modalBody;
}

export function clearDistributorsList() {
    clearContents(elements.distributorList);
}

export function showLoader() {
    elements.distributorList.appendChild(
        createElement('div', {
            className: 'loader',
            contents: createElement('div', { className: 'indicator' })
        })
    );
}

/**
 * Show no result message instead of dealer list.
 *
 * @param {string} message
 */
export function renderNoResults(message) {
    clearDistributorsList();

    elements.distributorList.appendChild(createElement('div', {
        className: 'cc-no-result',
        innerText: message
    }));
}

const SEARCH_FORM_HAS_ERROR_CLASS = 'cc-search-block_has_error';

export function showValidationMessage() {
    const form = elements.searchForm;

    if (!form.classList.contains(SEARCH_FORM_HAS_ERROR_CLASS)) {
        form.classList.add(SEARCH_FORM_HAS_ERROR_CLASS);
    }
}

export function hideValidationMessage() {
    elements.searchForm.classList.remove(SEARCH_FORM_HAS_ERROR_CLASS);
}

/**
 * Show yellow geolocated ZIP code message.
 *
 * @param {string} message
 * @returns {HTMLElement}
 */
function renderAlert(message) {
    return createElement('div', {
        className: 'cc-alert cc-alert_type_warning',
        contents: [
            createElement('span', {
                className: 'cc-alert__icon vertical_middle',
                contents: createPesIcon({
                    iconName: 'general_danger_wired',
                    size: 'SMALL',
                    className: 'figure-icon'
                })
            }),
            createElement('div', {
                innerText: message,
                attributes: {
                    [AUTOTESTS_ID_ATTRIBUTE]: 'commerce-connector-warning-message'
                }
            })
        ]
    });
}

/**
 * Shows alert messages red or yellow.
 *
 * @param {object} params
 * @param {string} params.zipCode
 * @param {boolean} params.isDetected
 * @param {object} params.messages
 * @param {string} params.messages.autoDetectedZipCode
 * @param {string} params.messages.allDistributorsShown
 */
export function showZipCodeAlertMessage({ zipCode, isDetected, messages }) {
    const { autoDetectedZipCode, allDistributorsShown } = messages;

    let alert = null;

    if (zipCode && isDetected) {
        // Show warning geolocated ZIP code message.
        alert = renderAlert(autoDetectedZipCode);
    } else if (!zipCode) {
        // Show warning empty ZIP code message.
        alert = renderAlert(allDistributorsShown);
    }

    if (alert) {
        elements.distributorList.appendChild(alert);
    }
}

/**
 * Show gray redirect message.
 *
 * @param {string} message
 */
export function showRedirectWarning(message) {
    const warning = createElement('div', {
        className: 'cc-info',
        innerText: message,
        attributes: {
            [AUTOTESTS_ID_ATTRIBUTE]: 'commerce-connector-info-message'
        }
    });

    elements.distributorList.appendChild(warning);
}
